/**
 * Allowlist sanitizer for OPERATOR-AUTHORED inline rich text — the explorer
 * promo-card body (#1163 v2). Sibling of ./content-html (#1264/#1277) and
 * built from its machinery: the same input cap (`boundContentHtml`), the same
 * structural pre-guards (`guardContentTags` — tag-length limit plus
 * depth-aware drop-body removal) and the same drop-body tag set, so every
 * bypass shape those review rounds hardened against is handled identically
 * here. Only the ALLOWLIST differs, and it is deliberately much narrower:
 *
 * - Inline emphasis and line breaks ONLY (#1163: "bold/em/line breaks").
 *   A card body is a sentence or two on a promo tile — no paragraphs, no
 *   headings, no lists, and no links: the card carries its one destination
 *   through the schema-validated `linkUrl` policy (explorer/config.ts), and
 *   a second link channel inside the body would bypass that policy's
 *   scheme-specific rules.
 * - NO attributes, on any tag. There is nothing an inline emphasis tag
 *   legitimately needs, so `onTagAttr`/`safeAttrValue` hooks (and the whole
 *   href-decoding surface of the content sanitizer) never come into play.
 *
 * Three adjustments this file adds on top of the shared machinery (codex
 * rounds 2a/2b):
 *
 * - Comments are stripped BEFORE `guardContentTags`. js-xss removes comments
 *   after our structural guards run, so a comment-split tag name
 *   (`<scr<!-- -->ipt>`) would reassemble into a real drop-body opener only
 *   once the depth bookkeeping was already over. The strip loops until
 *   stable because removing one comment can splice another together.
 * - The guard itself runs to a bounded FIXPOINT: one pass can splice a
 *   drop-body opener out of a partial plus a dropped closer
 *   (`<scr</style>ipt>`), so the guard repeats until its output stops
 *   changing, failing closed at the bound.
 * - The filter output is re-BALANCED. An unclosed `<strong>` handed to a
 *   `dangerouslySetInnerHTML` sink bleeds formatting past the card under
 *   SSR full-document parsing (the adoption agency reconstructs formatting
 *   elements beyond the container) while CSR fragment parsing stays scoped —
 *   a style leak and a hydration mismatch at once. The balancer is sound
 *   here ONLY because the output alphabet is closed: after js-xss, the
 *   string contains allowlisted no-attribute tags and escaped text, nothing
 *   else — this is not, and must not grow into, a general HTML parser.
 *
 * Non-allowlisted content never reaches the DOM as markup: disallowed tags
 * are removed with their text preserved, drop-body elements take their
 * contents with them, and js-xss escapes stray text brackets — the same
 * strip-don't-echo policy the #1277 reviews settled on for operator copy.
 * Pure string processing (no DOM), so the SAME function runs in the widget's
 * `renderToString` SSR path, the browser mount and plain Node tests, and
 * sanitizing at the render boundary keeps every visitor safe regardless of
 * what a compromised dashboard login managed to store.
 */
// Default import, not named: js-xss is CommonJS (see ./content-html).
import xssModule from 'xss'
const { FilterXSS } = xssModule as unknown as typeof import('xss')
import { boundContentHtml, DROP_BODY_TAGS, guardContentTags } from './content-html'

/** Tags a promo-card body may contain — nothing else, with no attributes. */
export const INLINE_HTML_ALLOWED_TAGS = ['strong', 'b', 'em', 'i', 'br'] as const

/** Deepest formatting nesting the balancer keeps; openers beyond it are dropped. */
export const MAX_INLINE_NESTING_DEPTH = 32

const filter = new FilterXSS({
  // Empty attribute lists: every attribute on an allowlisted tag is dropped.
  whiteList: Object.fromEntries(INLINE_HTML_ALLOWED_TAGS.map((tag) => [tag, []])),
  // Disallowed tags vanish (not escaped into visible `&lt;div&gt;` noise);
  // the drop-body set takes its text content along.
  stripIgnoreTag: true,
  stripIgnoreTagBody: DROP_BODY_TAGS,
})

/**
 * Raw C0/C1 control characters are stripped in the SHARED core, before the
 * filter, so both projections and the emitted HTML agree on one reading
 * (final round): tab/LF/CR stay — they are real whitespace — and everything
 * else in C0, DEL and C1 is removed with NO replacement. NUL in particular
 * never reaches the SSR string or the CSR DOM, which parsers treat
 * differently. A control-split tag name (`<scr` + NUL + `ipt>`) reassembles
 * into a drop-body opener the guard then handles — removal in that
 * direction only ever removes MORE, per the guard's own contract. Built
 * with fromCodePoint so this file carries no raw control bytes.
 */
const RAW_CONTROLS_RE = new RegExp(
  '[' + String.fromCodePoint(0x0000) + '-' + String.fromCodePoint(0x0008) +
  String.fromCodePoint(0x000b) + String.fromCodePoint(0x000c) +
  String.fromCodePoint(0x000e) + '-' + String.fromCodePoint(0x001f) +
  String.fromCodePoint(0x007f) + '-' + String.fromCodePoint(0x009f) + ']',
  'g',
)

/**
 * Remove HTML comments before the structural guards run (codex finding 4).
 * Loops because stripping `<!-- x -->` out of `<!<!-- x -->-- y -->` splices
 * a NEW comment together; legitimate copy is stable in one pass. If the
 * bound is ever exhausted with full comments still present — a splice chain
 * deeper than 20 is never authored content — fail closed rather than hand a
 * half-stripped string to the guards.
 */
function stripInlineComments(html: string): string {
  for (let i = 0; i < 20; i++) {
    const next = html.replace(/<!--[\s\S]*?-->/g, '')
    if (next === html) return html
    html = next
  }
  return /<!--[\s\S]*?-->/.test(html) ? '' : html
}

/**
 * Run guardContentTags to a bounded stable result (codex finding 6). A
 * single pass can SPLICE a drop-body opener together: on
 * `<scr</style>ipt>alert(1)</script>` the pass emits the `<scr` partial as
 * text, drops the `</style>` closer, and hands js-xss a reassembled
 * `<script>` opener its stripper then mishandles. Legitimate copy is stable
 * after one pass (the guard emits clean tags verbatim); a chain that is
 * still churning at the bound is never authored content — fail closed, the
 * same shape as the comment strip above.
 */
function guardInlineTagsStable(html: string): string {
  for (let i = 0; i < 10; i++) {
    const next = guardContentTags(html)
    if (next === html) return html
    html = next
  }
  return ''
}

/**
 * Token pattern for the CLOSED post-filter alphabet: js-xss has already
 * removed or escaped everything except allowlisted, attribute-less tags, so
 * every `<` that survives belongs to one of these. Escaped text (`&lt;b&gt;`)
 * never matches — there is no raw `<` in it.
 */
const INLINE_TAG_TOKEN_RE = /<(\/?)(strong|b|em|i|br)\b[^>]*>/gi

/**
 * Balance the filter output (codex finding 1): drop unmatched closers, close
 * still-open tags at the end of input, cap nesting depth, and untangle
 * cross-nesting (`<b><i>x</b>y</i>`) the way the browser adoption agency
 * would — close the intervening tags, emit the closer, re-open the
 * intervening ones — so SSR document parsing and CSR fragment parsing agree
 * on the same tree. `br` is void: openers pass through, closers are dropped.
 */
function balanceInlineTags(html: string): string {
  let out = ''
  let last = 0
  const open: string[] = []
  for (const m of html.matchAll(INLINE_TAG_TOKEN_RE)) {
    out += html.slice(last, m.index)
    last = m.index + m[0].length
    const name = m[2].toLowerCase()
    const isCloser = m[1] === '/'
    if (name === 'br') {
      if (!isCloser) out += m[0]
      continue
    }
    if (!isCloser) {
      if (open.length >= MAX_INLINE_NESTING_DEPTH) continue
      open.push(name)
      out += m[0]
      continue
    }
    const at = open.lastIndexOf(name)
    if (at === -1) continue
    const reopen = open.slice(at + 1)
    for (let k = open.length - 1; k > at; k--) out += `</${open[k]}>`
    out += `</${name}>`
    open.length = at
    for (const tag of reopen) {
      open.push(tag)
      out += `<${tag}>`
    }
  }
  out += html.slice(last)
  for (let k = open.length - 1; k >= 0; k--) out += `</${open[k]}>`
  return out
}

/**
 * Sanitize an operator-authored promo-card body for a
 * `dangerouslySetInnerHTML` sink. Returns "" for non-strings / blank input;
 * otherwise the input reduced to the inline allowlist above, comment-free
 * and tag-balanced. Newlines are preserved as text — the card copy renders
 * `white-space: pre-line`, so authored line breaks keep working alongside
 * `<br>`. Output stays within the content sanitizer's documented
 * escape-aware bound: no attributes and no `rel` injection here, and the
 * balancer's additions are capped (at most MAX_INLINE_NESTING_DEPTH closers
 * at the end, and per-closer churn bounded by the same depth), so the plain
 * MAX_CONTENT_HTML_EXPANSION factor over the capped input still holds.
 */
export function sanitizeInlineHtml(value: unknown): string {
  if (typeof value !== 'string') return ''
  const v = guardInlineTagsStable(
    stripInlineComments(boundContentHtml(value.trim()).replace(RAW_CONTROLS_RE, '')),
  )
  if (v === '') return ''
  return balanceInlineTags(filter.process(v))
}

/**
 * Named references the visible-text decoder knows — a curated subset of the
 * HTML spec's named list, each entry verified against
 * https://html.spec.whatwg.org/entities.json and matched CASE-SENSITIVELY,
 * exactly as that list is keyed (round 3): `&NbSp;` is not a spec entry and
 * renders literally in a browser, so it must stay literal here too, or an
 * authored visible body could read as empty and lose its grid slot. The
 * all-caps legacy forms below (AMP/LT/GT/QUOT/COPY/REG/TRADE) ARE spec
 * entries; uppercase APOS/NBSP are not. `Tab;`/`NewLine;` are the spec's
 * exact-case whitespace refs.
 *
 * ACCEPTED DIVERGENCE (round-3 ruling): deliberately NOT the full
 * 2231-entry spec table. An unknown named ref stays literal, which is the
 * safe direction for both consumers of this projection: literal text
 * renders visibly, so the card correctly KEEPS its slot, and the label
 * voices the entity source — cosmetic, never a disappearing card.
 * Semicolonless NAMED legacy forms (`&amp` bare) stay out of scope on the
 * same argument — except the invisible-valued ones admitted in round 4
 * (see the tokenizer), where literal-kept is the WRONG direction.
 */
const VISIBLE_NAMED_REFS: Record<string, string> = {
  lt: '<', gt: '>', quot: '"', apos: "'", amp: '&',
  LT: '<', GT: '>', QUOT: '"', AMP: '&',
  copy: '©', reg: '®', trade: '™',
  COPY: '©', REG: '®', TRADE: '™',
  mdash: '—', ndash: '–', hellip: '…',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
}

/**
 * Named references whose RENDERED value is entirely whitespace or
 * default-ignorable — the counterexample class to the literal-kept safe
 * direction: a literal-kept `&ZeroWidthSpace;` LOOKS like content to the
 * projection while rendering nothing, which is exactly the blank-card class
 * the projection exists to catch.
 *
 * GENERATED by criterion, not hand-curated (round 6): every entry of
 * https://html.spec.whatwg.org/entities.json (semicolon-terminated keys)
 * whose expansion consists entirely of characters matching
 * /^(?:\s|\p{Default_Ignorable_Code_Point})+$/u — 33 names as generated
 * against the spec list on 2026-09-01, case-sensitive as that list is
 * keyed; values are the spec expansions (code points verified there too);
 * regenerate with the same one-liner if the spec list ever grows.
 *
 * Values are the REAL expansions (fresh pass), written via fromCodePoint so
 * this file carries no raw invisible bytes. The decoder stays SHARED and
 * truthful; each projection's own final character pass then decides what an
 * invisible means for ITS job — the emptiness projection maps every
 * default-ignorable to a collapsible space (so `&ZeroWidthSpace;` can
 * never masquerade as content), while the label projection keeps joiners
 * and drops the silent ones with no replacement (so `&shy;` cannot split a
 * word and `&zwj;` still joins an emoji). Exported so the test suite can
 * enforce the criterion over every entry forever (nbsp/Tab/NewLine live
 * here rather than in the hand map above — one source of truth).
 */
export const INVISIBLE_NAMED_REFS: Record<string, string> = {
  ApplyFunction: String.fromCodePoint(0x2061),
  InvisibleComma: String.fromCodePoint(0x2063),
  InvisibleTimes: String.fromCodePoint(0x2062),
  MediumSpace: String.fromCodePoint(0x205f),
  NegativeMediumSpace: String.fromCodePoint(0x200b),
  NegativeThickSpace: String.fromCodePoint(0x200b),
  NegativeThinSpace: String.fromCodePoint(0x200b),
  NegativeVeryThinSpace: String.fromCodePoint(0x200b),
  NewLine: String.fromCodePoint(0x000a),
  NoBreak: String.fromCodePoint(0x2060),
  NonBreakingSpace: String.fromCodePoint(0x00a0),
  Tab: String.fromCodePoint(0x0009),
  ThickSpace: String.fromCodePoint(0x205f, 0x200a),
  ThinSpace: String.fromCodePoint(0x2009),
  VeryThinSpace: String.fromCodePoint(0x200a),
  ZeroWidthSpace: String.fromCodePoint(0x200b),
  af: String.fromCodePoint(0x2061),
  emsp: String.fromCodePoint(0x2003),
  emsp13: String.fromCodePoint(0x2004),
  emsp14: String.fromCodePoint(0x2005),
  ensp: String.fromCodePoint(0x2002),
  hairsp: String.fromCodePoint(0x200a),
  ic: String.fromCodePoint(0x2063),
  it: String.fromCodePoint(0x2062),
  lrm: String.fromCodePoint(0x200e),
  nbsp: String.fromCodePoint(0x00a0),
  numsp: String.fromCodePoint(0x2007),
  puncsp: String.fromCodePoint(0x2008),
  rlm: String.fromCodePoint(0x200f),
  shy: String.fromCodePoint(0x00ad),
  thinsp: String.fromCodePoint(0x2009),
  zwj: String.fromCodePoint(0x200d),
  zwnj: String.fromCodePoint(0x200c),
}
for (const [name, expansion] of Object.entries(INVISIBLE_NAMED_REFS)) {
  VISIBLE_NAMED_REFS[name] = expansion
}

/**
 * Windows-1252 remap for numeric references in 0x80-0x9F — the 27-entry
 * table from the HTML spec's "numeric character reference end state"
 * (round 3). The five slots absent here (0x81, 0x8D, 0x8F, 0x90, 0x9D) are
 * NOT remapped by the spec and fall through to the controls-to-space rule.
 */
const C1_REMAP: Record<number, string> = {
  0x80: '€', 0x82: '‚', 0x83: 'ƒ', 0x84: '„', 0x85: '…',
  0x86: '†', 0x87: '‡', 0x88: 'ˆ', 0x89: '‰', 0x8a: 'Š',
  0x8b: '‹', 0x8c: 'Œ', 0x8e: 'Ž', 0x91: '‘', 0x92: '’',
  0x93: '“', 0x94: '”', 0x95: '•', 0x96: '–', 0x97: '—',
  0x98: '˜', 0x99: '™', 0x9a: 'š', 0x9b: '›', 0x9c: 'œ',
  0x9e: 'ž', 0x9f: 'Ÿ',
}

/**
 * ONE-pass, one-layer character-reference decoder for the visible-text
 * projection (codex finding 5, spec-bounded per the round-3 ruling) — no
 * DOM, so it is SSR-safe. A single left-to-right pass means `&amp;lt;`
 * decodes to the literal `&lt;` and stops, the same one-layer reading the
 * browser gives the rendered text.
 *
 * NUMERIC references follow the data state: the semicolon is optional
 * (`&#32` decodes, longest digit run wins), the 0x80-0x9F range remaps
 * through the Windows-1252 table, and NUL / surrogates / out-of-range
 * decode to U+FFFD — the browser's replacement glyph, which IS visible and
 * therefore counts as content. Remaining C0/C1 controls (which browsers do
 * decode to controls) voice as a space; tab/LF/CR are real whitespace
 * either way. NAMED references require the semicolon and match
 * case-sensitively against the curated map above, via an own-property
 * check — a prototype key (`&constructor;`) is not a reference.
 *
 * The third alternation branch (round 4) admits exactly TWO bare named
 * forms, `&nbsp` and `&shy` — the spec's legacy semicolonless list entries
 * whose value is invisible (verified against entities.json: `&nbsp` U+00A0
 * and `&shy` U+00AD are both bare-valid; `&ensp`/`&thinsp`/`&zwj` bare are
 * not). Visible-valued legacy bare forms (`&amp`, `&copy`) stay literal —
 * the general semicolonless-named mechanism remains out of scope. Ordering
 * matters: the semicolon branch is tried first, so `&nbsp;` still resolves
 * as the full reference, never as bare-`&nbsp` plus a stray semicolon.
 */
function decodeVisibleRefs(text: string): string {
  return text.replace(
    /&(#\d+|#[xX][0-9a-fA-F]+);?|&([A-Za-z][A-Za-z0-9]*);|&(?:nbsp|shy)/g,
    (match, numeric: string | undefined, named: string | undefined) => {
      if (numeric !== undefined) {
        const hex = numeric[1] === 'x' || numeric[1] === 'X'
        const cp = hex ? parseInt(numeric.slice(2), 16) : parseInt(numeric.slice(1), 10)
        if (!Number.isFinite(cp) || cp <= 0 || cp > 0x10ffff) return '�'
        if (cp >= 0xd800 && cp <= 0xdfff) return '�'
        const remapped = C1_REMAP[cp]
        if (remapped !== undefined) return remapped
        if ((cp < 0x20 && cp !== 0x09 && cp !== 0x0a && cp !== 0x0d) || (cp >= 0x7f && cp <= 0x9f)) return ' '
        return String.fromCodePoint(cp)
      }
      if (named !== undefined) {
        return Object.prototype.hasOwnProperty.call(VISIBLE_NAMED_REFS, named)
          ? VISIBLE_NAMED_REFS[named]
          : match
      }
      // Third branch: the bare legacy forms decode to their REAL values,
      // exactly like their semicolon twins; each projection's final
      // character pass decides what the invisible means for its job.
      return match === '&shy' ? VISIBLE_NAMED_REFS.shy : VISIBLE_NAMED_REFS.nbsp
    },
  )
}

/**
 * The VISIBLE text of a rich body — what a visitor actually reads once the
 * sanitizer has run (codex findings 2 + 3). Sanitize first, so removed
 * elements (a `<script>` body, a stripped tag's attributes) contribute
 * nothing; then drop the known-balanced allowlisted tags, decode character
 * references one layer (finding 5: `&#32;` is whitespace, `&#x53;ale` reads
 * "Sale"), and collapse whitespace. Used for the is-this-card-empty checks
 * (`activeMarketingCards`, `marketingCardNeedsAccessibleLabel`) and the
 * accessible-name fallback — never for markup emission. A `<br>`-only body
 * reads as empty: it renders nothing a visitor (or screen reader) can
 * perceive.
 *
 * CLASS CLOSURE (rounds 5-6): one post-decode normalization maps every
 * invisible code point to a plain space before the collapse. This closes
 * ALL routes to an invisible body at once — named refs (already
 * space-mapped; harmless double-cover), NUMERIC refs (`&#8203;`, `&#8290;`),
 * the bare legacy forms, and a RAW invisible character pasted into the
 * stored string, which is no reference at all and survives decode
 * untouched. Round 6 replaced the hand-rolled range (punctured by the
 * U+2061-2064 invisible operators) with the ENGINE-MAINTAINED
 * `\p{Default_Ignorable_Code_Point}` binary property — in the ECMAScript
 * supported-property list, verified to compile and match U+2062 on this
 * repo's Node — so the set is closed by criterion, not enumeration
 * (soft hyphen, zero-widths, bidi marks, word joiner, invisible operators,
 * ZWNBSP, variation selectors, plane-14 tags, all of it). The `\s`-covered
 * space blocks (U+2000-200A / U+205F / U+3000) need no help from it.
 */
export function inlineHtmlVisibleText(value: unknown): string {
  const html = sanitizeInlineHtml(value)
  if (html === '') return ''
  return decodeVisibleRefs(html.replace(/<[^>]*>/g, ' '))
    .replace(/\p{Default_Ignorable_Code_Point}/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Invisibles the ACCESSIBLE projection keeps: ZWJ/ZWNJ (emoji and script
 * joins), the variation selectors (glyph presentation), and the plane-14
 * TAG characters U+E0001 / U+E0020-U+E007F (final round: a subdivision
 * flag — England is U+1F3F4 plus a TAG-letter sequence — must voice as the
 * flag, not a bare black flag). Removing any of them breaks a sequence a
 * screen reader would voice as one thing. The EMPTINESS projection still
 * strips tags: a tags-only body shows nothing without a visible base, and
 * the base U+1F3F4 is visible content on its own, so emptiness semantics
 * are already right. Built with fromCodePoint so this file carries no raw
 * invisible bytes.
 */
const ACCESSIBLE_KEPT_INVISIBLES = new RegExp(
  '[' + String.fromCodePoint(0x200c) + String.fromCodePoint(0x200d) +
  String.fromCodePoint(0xfe00) + '-' + String.fromCodePoint(0xfe0f) +
  String.fromCodePoint(0xe0100) + '-' + String.fromCodePoint(0xe01ef) +
  String.fromCodePoint(0xe0001) +
  String.fromCodePoint(0xe0020) + '-' + String.fromCodePoint(0xe007f) + ']',
  'u',
)

/**
 * The ACCESSIBLE text of a rich body — the aria-label projection (fresh
 * pass). Shares the sanitize + decode core with `inlineHtmlVisibleText`;
 * the two differ exactly where their jobs differ:
 *
 * - Inline emphasis tags strip with NO separator — `<strong>` is emphasis,
 *   not a word boundary (`Pre<strong>pay</strong>` voices "Prepay", never
 *   "Pre pay"); only `<br>` reads as a space.
 * - ZWJ/ZWNJ and variation selectors are PRESERVED, so an emoji sequence
 *   voices as one glyph — through every authoring route: raw characters,
 *   numeric refs, and the named `&zwj;`/`&zwnj;` aliases (the shared
 *   decoder emits real expansions, so the kept-set check sees them all).
 * - The remaining default-ignorables (soft hyphen, zero-widths, bidi
 *   marks, word joiner, invisible operators, ZWNBSP, plane-14 tags) are
 *   removed with NO replacement — a soft hyphen inside a word must not
 *   split it.
 *
 * The EMPTINESS callers must keep using `inlineHtmlVisibleText`: dropping
 * invisibles with no replacement is right for a label and wrong for the
 * is-this-card-blank check, where every invisible must become collapsible
 * whitespace so it can never masquerade as content.
 */
export function inlineHtmlAccessibleText(value: unknown): string {
  const html = sanitizeInlineHtml(value)
  if (html === '') return ''
  return decodeVisibleRefs(html.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, ''))
    .replace(/\p{Default_Ignorable_Code_Point}/gu, (ch) => (ACCESSIBLE_KEPT_INVISIBLES.test(ch) ? ch : ''))
    .replace(/\s+/g, ' ')
    .trim()
}
